import { useEffect, useState } from 'react';

import fetchEmailTemplates from 'lib/api/fetchEmailTemplates';
import {
  EmailTemplate,
  EmailTemplateAccessTier,
  TemplateType,
} from 'types/EmailTemplates';
import { Maybe } from 'types/UtilityTypes';

import { FALLBACK_BATCH_LTL_TEMPLATE } from '../constants';

const ACCESS_TIER_PRIORITY = [
  EmailTemplateAccessTier.USER,
  EmailTemplateAccessTier.GROUP,
  EmailTemplateAccessTier.SERVICE,
  EmailTemplateAccessTier.GENERIC,
];

// Batch LTL reply template body; the most specific tier wins, like the single LTL template.
export function useBatchLTLTemplate(): string {
  const [templateBody, setTemplateBody] = useState<Maybe<string>>(null);

  // Fetch once per panel mount; ignore the response if the panel unmounted.
  useEffect(() => {
    let cancelled = false;

    fetchEmailTemplates({
      templateType: TemplateType.QUICK_QUOTE_BATCH_LTL,
    }).then((result) => {
      if (cancelled || result.isErr()) {
        return;
      }

      setTemplateBody(pickTemplateBody(result.value.templates ?? []));
    });

    return () => {
      cancelled = true;
    };
  }, []);

  return templateBody || FALLBACK_BATCH_LTL_TEMPLATE;
}

function pickTemplateBody(templates: EmailTemplate[]): Maybe<string> {
  const withBody = templates.filter((template) => template.body?.trim());
  const [best] = [...withBody].sort(
    (a, b) =>
      getTierRank(a.templateAccessTier) - getTierRank(b.templateAccessTier)
  );

  return best?.body ?? null;
}

function getTierRank(tier: Maybe<EmailTemplateAccessTier> | undefined): number {
  const rank = tier ? ACCESS_TIER_PRIORITY.indexOf(tier) : -1;

  return rank === -1 ? ACCESS_TIER_PRIORITY.length : rank;
}
